import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { message } from '@brokerbay/design-components';
import { getKeyholderBillingProfile } from 'api/SupraController';

// ============================================================================
// Types
// ============================================================================

export interface BillingProfile {
  BoardBillingNumber: string;
  UserBillingNumber: string;
  MaskedCCNumber?: string;
  CCType?: string;
  CCExpiryDate?: string;
  CardholderName?: string;
}

interface SubscriptionsPageContextShape {
  billingProfile: BillingProfile | null;
  loadingBillingProfile: boolean;
  refetchBillingProfile: () => Promise<void>;
}

interface SubscriptionsPageProviderProps {
  orgId: string;
  billingCustomerNumber?: string;
  children: React.ReactNode;
}

// ============================================================================
// Context
// ============================================================================

const SubscriptionsPageContext =
  createContext<SubscriptionsPageContextShape | null>(null);

/**
 * Hook to consume SubscriptionsPageContext.
 * Throws error if used outside of provider.
 */
export function useSubscriptionsPageContext(): SubscriptionsPageContextShape {
  const context = useContext(SubscriptionsPageContext);
  if (!context) {
    throw new Error(
      'useSubscriptionsPageContext must be used within a SubscriptionsPageProvider.',
    );
  }
  return context;
}

// ============================================================================
// Provider
// ============================================================================

/**
 * Provides shared billing profile data to all components on the Subscriptions & Payments page.
 * Fetches billing profile once and shares it with child components.
 */
export const SubscriptionsPageProvider: React.FC<
  SubscriptionsPageProviderProps
> = ({ orgId, billingCustomerNumber, children }) => {
  const [billingProfile, setBillingProfile] = useState<BillingProfile | null>(
    null,
  );
  const [loadingBillingProfile, setLoadingBillingProfile] = useState(false);
  const [billingProfileError, setBillingProfileError] = useState<Error | null>(
    null,
  );

  const fetchBillingProfile = useCallback(async () => {
    if (!orgId || !billingCustomerNumber) {
      setBillingProfile(null);
      message.error(
        'Unable to load billing information. Please contact support.',
      );
      return;
    }

    setLoadingBillingProfile(true);
    setBillingProfileError(null);

    try {
      const profile = await getKeyholderBillingProfile({
        orgId,
        billingCustomerNumber,
      });

      setBillingProfile(profile);
    } catch (error) {
      const err =
        error instanceof Error
          ? error
          : new Error('Failed to fetch billing profile');
      setBillingProfileError(err);
      setBillingProfile(null);
      message.error('Failed to load billing information.');
    } finally {
      setLoadingBillingProfile(false);
    }
  }, [orgId, billingCustomerNumber]);

  useEffect(() => {
    fetchBillingProfile();
  }, [fetchBillingProfile]);

  return (
    <SubscriptionsPageContext.Provider
      value={{
        billingProfile,
        loadingBillingProfile,
        refetchBillingProfile: fetchBillingProfile,
      }}
    >
      {children}
    </SubscriptionsPageContext.Provider>
  );
};

export default SubscriptionsPageContext;
